import { Link } from 'react-router';
import {
  GuideLayout,
  GuideSection,
  GuideTip,
  GuideSteps,
  GuideStep,
  GuideGrid,
  GuideCard,
  GuideDarkSection,
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function FixMyStreetPotholeReportingGuide() {
  return (
    <GuideLayout
      slug="fix-my-street-pothole-reporting"
      category="Getting Started"
      categoryColor="brand"
      title="Fix My Street: How to Report a Pothole in Your Area"
      description="How FixMyStreet works, how to report a pothole, and why third-party entries must be verified against authority records."
      readTime="5 min"
      updatedDate="2026-07-19"
      datePublished="2026-07-09"
      reviewedFor="UK reporting coverage and England/Wales claim context"
      showCouncilDataCallout
      sources={[
        { name: 'FixMyStreet', url: 'https://www.fixmystreet.com' },
        { name: 'mySociety', url: 'https://www.mysociety.org' },
      ]}
      relatedGuides={[
        { slug: 'prior-reports-explained', title: 'Why Prior Reports Are So Important', readTime: '5 min' },
        { slug: 'what-makes-a-valid-claim', title: 'What Makes a Valid Pothole Claim?', readTime: '5 min' },
        { slug: 'who-is-responsible', title: 'Who Is Responsible for the Road?', readTime: '4 min' },
      ]}
      atAGlance={[
        { label: 'What it is', value: 'A free, independent UK-wide reporting site run by the charity mySociety — not by your council' },
        { label: 'Coverage', value: 'Every council area in the UK — one site works whether you\u2019re in Dudley, Hackney, Kirklees or anywhere else' },
        { label: 'How it works', value: 'Enter a postcode or street name, pin the pothole on the map, add a photo — it routes the report to the right council automatically' },
        { label: 'Evidence limit', value: 'A public entry is a lead to verify, not proof the authority received it, matched it to the same defect or incurred liability' },
        { label: 'Reporting vs claiming', value: 'A FixMyStreet report asks for a repair; it does not claim compensation. If the pothole damaged your car, that\u2019s a separate claim' },
      ]}
      howTo={{
        name: 'How to Report a Pothole on FixMyStreet',
        description:
          'Four steps to create a FixMyStreet entry and retain the details needed to verify it with the responsible authority.',
        steps: [
          {
            name: 'Search your location',
            text: 'Go to fixmystreet.com and enter the postcode or street name where the pothole is. Treat any nearby entry as a research lead until its defect, location, date and receipt are verified.',
          },
          {
            name: 'Pin the exact spot',
            text: 'Click the precise location of the pothole on the map. Accuracy matters — the council\u2019s inspector needs to find it, and if you later claim, the location links your report to your incident.',
          },
          {
            name: 'Describe it and add photos',
            text: 'Select the pothole category, describe its size and depth (depth matters most — many councils use a 40mm intervention level), and attach clear photos.',
          },
          {
            name: 'Submit — FixMyStreet routes it automatically',
            text: 'You don\u2019t need to know which council maintains the road. FixMyStreet works out the responsible authority from the location and sends the report to them, then tracks its status publicly.',
          },
        ],
      }}
      faqs={[
        {
          question: 'Is FixMyStreet run by my council?',
          answer: 'No. FixMyStreet is run by mySociety, a UK charity, and is independent of councils. It says it forwards reports to the relevant authority, but verify receipt and the corresponding defect log with that authority.',
        },
        {
          question: 'Does FixMyStreet cover my area?',
          answer: 'Yes — FixMyStreet covers every council area in the UK. There is no separate site per area: whether you search "fix my street Dudley" or "fix my street Hackney", it\u2019s the same fixmystreet.com, and it routes reports to the right council based on the location you pin.',
        },
        {
          question: 'Does reporting a pothole get me compensation?',
          answer: 'No — a report asks the council to repair the road. If the pothole has damaged your vehicle, compensation requires a separate claim to the council under Section 41 of the Highways Act 1980. You can use our free claim letter template or let Fixtyer build the full claim pack.',
        },
        {
          question: 'I found a nearby FixMyStreet entry. Does that help?',
          answer: 'It is a research lead. Verify the same defect, location and date, the responsible authority, report status and receipt, and the authority’s own records before relying on it. The public entry alone does not prove notice or liability.',
        },
        {
          question: 'Should I report the pothole even after it damaged my car?',
          answer: 'You can report it promptly to request a repair and retain the entry details. The report and compensation claim are separate, and the authority’s own receipt and action records should still be checked.',
        },
        {
          question: 'Does Fixtyer check FixMyStreet for me?',
          answer: 'Mac can search FixMyStreet for nearby leads. A nearby match does not prove it is the same defect or that the authority received it, so verify it before relying on it.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        }
        title="What FixMyStreet Actually Is"
      >
        <p className="mb-4">
          <a
            href="https://www.fixmystreet.com"
            target="_blank"
            rel="noopener noreferrer"
            className={inlineLink}
          >
            FixMyStreet
          </a>{' '}
          is a free website run by the UK charity mySociety that lets anyone report street
          problems — potholes, broken pavements, faded markings — without needing to know which
          authority is responsible. You pin the problem on a map, and FixMyStreet works out{' '}
          <Link to="/guides/who-is-responsible" className={inlineLink}>
            which council or highway authority maintains that road
          </Link>{' '}
          and sends the report to them.
        </p>
        <p className="mb-4">
          Crucially, it's one site for the whole country. People often search for "fix my street"
          plus their town — but whether you're in{' '}
          <Link to="/councils/dudley" className={inlineLink}>
            Dudley
          </Link>
          ,{' '}
          <Link to="/councils/hackney" className={inlineLink}>
            Hackney
          </Link>
          ,{' '}
          <Link to="/councils/kirklees" className={inlineLink}>
            Kirklees
          </Link>{' '}
          or anywhere else, it's the same fixmystreet.com, and it routes your report to the right
          council automatically.
        </p>
        <GuideTip variant="info" title="Not affiliated — but connected to your rights">
          Fixtyer has no connection to FixMyStreet. Its entries can provide useful location leads, but
          they do not establish council receipt, notice or liability without verification.
        </GuideTip>
      </GuideSection>

      <GuideSteps>
        <GuideStep number={1} title="Search Your Location">
          <p className="mb-2">
            Visit{' '}
            <a
              href="https://www.fixmystreet.com"
              target="_blank"
              rel="noopener noreferrer"
              className={inlineLink}
            >
              fixmystreet.com
            </a>{' '}
            and enter the postcode or street name. The map shows existing reports nearby — worth
            checking before you post. Treat a nearby entry as a lead and ask the authority to confirm receipt
            and the corresponding defect record.
          </p>
        </GuideStep>

        <GuideStep number={2} title="Pin the Exact Spot">
          <p className="mb-2">
            Click the precise location of the pothole. Be accurate — the council's inspector
            needs to find it from your pin, and if the pothole later damages a vehicle, the
            location is what links a report to an incident.
          </p>
        </GuideStep>

        <GuideStep number={3} title="Describe It and Add Photos">
          <p className="mb-2">
            Choose the pothole category, describe the size and depth, and attach photos. Depth is
            the detail councils act on — many use a 40mm intervention level. A photo with
            something for scale makes the report much harder to ignore.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Submit and Track">
          <p className="mb-2">
            FixMyStreet says it sends the report to the responsible authority and shows a public
            status such as open, scheduled or fixed. Save the URL and facts as research leads, then
            verify receipt, defect identity and action against the authority&apos;s own records.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
        }
        title="How to verify a report"
      >
        <div className="space-y-4">
          <p className="text-brand-200">
            Councils defend pothole claims using the{' '}
            <Link to="/guides/section-58-defence" className="text-accent-400 hover:text-accent-300 font-medium underline underline-offset-2">
              Section 58 defence
            </Link>
            . A FixMyStreet entry is a dated, public, independently hosted record, but it does not itself
            establish that the authority received it or matched it to the same defect.
          </p>
          <div className="p-4 bg-brand-900/50 rounded-xl border-l-4 border-success-500">
            <p className="text-white font-medium mb-1">The practical effect:</p>
            <p className="text-brand-300">
              Ask the authority for its report log, inspection record and action history. Compare dates,
              coordinates and descriptions before relying on{' '}
              <Link to="/guides/prior-reports-explained" className="text-accent-400 hover:text-accent-300 font-medium underline underline-offset-2">
                prior reports are so important
              </Link>
              .
            </p>
          </div>
        </div>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        }
        title="Reporting vs. Claiming — Two Different Things"
      >
        <GuideGrid cols={2}>
          <GuideCard title="A FixMyStreet report">
            Asks the council to <em>repair the road</em>. It's free, takes two minutes, and helps
            everyone who drives that road. It does not ask for — or lead to — compensation.
          </GuideCard>
          <GuideCard title="A pothole damage claim" variant="accent">
            Asks the council to <em>pay for your damage</em> under Section 41 of the Highways Act
            1980. It needs incident and damage evidence; a verified authority report may also be
            relevant depending on what the location-specific records show.
          </GuideCard>
        </GuideGrid>
        <p className="mb-4">
          If a pothole has damaged your car, do both: report the pothole so it gets fixed, then
          claim for the damage. You can write the claim yourself using our{' '}
          <Link to="/guides/pothole-claim-letter-template" className={inlineLink}>
            free claim letter template
          </Link>
          , or check{' '}
          <Link to="/councils" className={inlineLink}>
            your council's claims process and road maintenance record
          </Link>{' '}
          first to see what you're up against.
        </p>
        <GuideTip variant="success" title="Mac can surface research leads">
          Mac can surface nearby FixMyStreet links and facts as research leads. Before relying on a
          result, verify the same defect, location and date, responsible authority, report status
          and receipt, and the authority&apos;s own records. A nearby match is not proof of notice.
        </GuideTip>
      </GuideSection>
    </GuideLayout>
  );
}
